import React, { useState } from 'react'
import { Box, Button, Divider, Grid, Typography, Paper } from '@mui/material'
import { PendingAccount } from 'types/accountTypes'
import PendingAccountForm from 'pages/AccountDetail/PendingAccountForm'

export const LabelValue = ({
  label,
  value
}: {
  label: string
  value: string | number | React.ReactNode
}) => (
  <Box mb={1}>
    <Typography variant='subtitle2' fontWeight={600}>
      {label}
    </Typography>
    <Typography variant='body1'>{value}</Typography>
  </Box>
)

const Section = ({ title }: { title: string }) => (
  <Box mt={2.5} mb={1.5}>
    <Typography variant='h6'>{title}</Typography>
    <Divider sx={{ mt: 1 }} />
  </Box>
)

const PendingAccountInfo = ({ data }: { data: PendingAccount }) => {
  const [isEditing, setIsEditing] = useState(false)

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        mb={3}
      >
        <Typography variant='h5'>Pending Account Info</Typography>
        <Button
          variant={isEditing ? 'outlined' : 'contained'}
          onClick={() => setIsEditing(prev => !prev)}
          sx={{ minWidth: 120 }}
          color={isEditing ? 'secondary' : 'primary'}
        >
          {isEditing ? 'Cancel Edit' : 'Edit'}
        </Button>
      </Box>

      {isEditing ? (
        <PendingAccountForm data={data} onCancel={() => setIsEditing(false)} />
      ) : (
        <>
          <Section title='Business Info' />
          <Grid container spacing={0}>
            <Grid item xs={12} sm={6}>
              <LabelValue label='ID' value={data.id} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue label='Company Name' value={data.companyName} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue label='Email' value={data.email} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue label='Phone' value={data.phoneNumber} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue
                label='Alternate Phone'
                value={data.alternatePhoneNumber}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue label='Sales Rep' value={data.salesRepresentative} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue label='Fax' value={data.fax} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue label='Tax ID' value={data.taxId} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue label='Business Type' value={data.businessType} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue
                label='Taxable'
                value={data.isTaxable ? 'Yes' : 'No'}
              />
            </Grid>
            {!data.isTaxable && (
              <>
                <Grid item xs={12} sm={6}>
                  <LabelValue
                    label='Expected Monthly Sales'
                    value={data.expectedMonthlySales}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LabelValue
                    label='Sales Tax Account'
                    value={data.salesTaxAccount}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LabelValue
                    label='Resale License'
                    value={
                      data.hasResaleLicense ? (
                        <>
                          Yes —{' '}
                          <Typography component='span' fontWeight='bold'>
                            {data.resaleLicenseNumber || 'N/A'}
                          </Typography>
                        </>
                      ) : (
                        'No'
                      )
                    }
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={6}>
              <LabelValue label='Notes' value={data.notes} />
            </Grid>
          </Grid>

          <Section title='Billing Address' />
          <Grid container spacing={0}>
            <Grid item xs={12} sm={6}>
              <LabelValue label='First Name' value={data.billingFirstName} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue label='Last Name' value={data.billingLastName} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue
                label='Address Line 1'
                value={data.billingAddressLine1}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue
                label='Address Line 2'
                value={data.billingAddressLine2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue label='City' value={data.billingCity} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue label='State' value={data.billingState} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue label='Country' value={data.billingCountry} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue label='Zip' value={data.billingZipCode} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue label='Phone' value={data.billingPhoneNumber} />
            </Grid>
          </Grid>

          <Section title='Shipping Address' />
          <Grid container spacing={0}>
            <Grid item xs={12} sm={6}>
              <LabelValue label='First Name' value={data.shippingFirstName} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue label='Last Name' value={data.shippingLastName} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue
                label='Address Line 1'
                value={data.shippingAddressLine1}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue
                label='Address Line 2'
                value={data.shippingAddressLine2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue label='City' value={data.shippingCity} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue label='State' value={data.shippingState} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue label='Country' value={data.shippingCountry} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue label='Zip' value={data.shippingZipCode} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValue label='Phone' value={data.shippingPhoneNumber} />
            </Grid>
          </Grid>
        </>
      )}
    </Paper>
  )
}

export default PendingAccountInfo
